import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  ArrowLeft, Briefcase, GraduationCap, ShieldCheck, History, Loader2,
  Calendar as CalendarIcon, Clock, MapPin, Ban, RotateCcw, ExternalLink,
  ChevronLeft, ChevronRight, Building2, User, Power, RefreshCw, CheckCircle2,
  AlertTriangle, Award, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { modeLabelFor } from '@/lib/experienceStatus';

const STATUS_COLORS = {
  pending: 'bg-amber-50 text-amber-700', approved: 'bg-emerald-50 text-emerald-600',
  rejected: 'bg-red-50 text-red-600', draft: 'bg-gray-100 text-gray-500',
  suspended: 'bg-orange-50 text-orange-700', deactivated: 'bg-slate-100 text-slate-600',
};

const BOOKING_STATUS_LABELS = {
  pending_parent_confirmation: 'Awaiting Parent Confirmation',
  confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled',
  pending: 'Pending', scheduled: 'Scheduled', no_show: 'No Show',
  rescheduled: 'Rescheduled',
};

const BOOKING_STATUS_COLORS = {
  confirmed: 'bg-emerald-50 text-emerald-600', completed: 'bg-blue-50 text-blue-600',
  cancelled: 'bg-red-50 text-red-600', pending_parent_confirmation: 'bg-amber-50 text-amber-700',
  pending: 'bg-amber-50 text-amber-700', scheduled: 'bg-sky-50 text-sky-600',
};

const fmtD = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export default function MentorDetailsPage() {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');
  const [error, setError] = useState('');

  // Action state
  const [approveConfirm, setApproveConfirm] = useState(false);
  const [suitabilityChecked, setSuitabilityChecked] = useState(false);
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [reinstateDialog, setReinstateDialog] = useState(false);
  const [reinstateReason, setReinstateReason] = useState('');
  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/coordinator/mentors/${mentorId}/details`);
      setDetail(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load mentor details');
    }
    setLoading(false);
  }, [mentorId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleApprove = async () => {
    if (!suitabilityChecked) return;
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/approve`, { suitability_confirmed: true });
      toast.success('Mentor approved'); setApproveConfirm(false); setSuitabilityChecked(false); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/reject`);
      toast.success('Mentor rejected'); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) return;
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/suspend`, { reason: suspendReason });
      toast.success('Mentor suspended'); setSuspendDialog(false); setSuspendReason(''); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleReinstate = async () => {
    if (!reinstateReason.trim()) return;
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/reinstate`, { reason: reinstateReason });
      toast.success('Mentor reinstated'); setReinstateDialog(false); setReinstateReason(''); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleDeactivate = async () => {
    if (!deactivateReason.trim()) return;
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/deactivate`, { reason: deactivateReason });
      toast.success('Mentor deactivated'); setDeactivateDialog(false); setDeactivateReason(''); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleReconsider = async () => {
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/reconsider`);
      toast.success('Application moved to pending'); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleApproveChanges = async () => {
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/approve-changes`);
      toast.success('Profile changes approved and published'); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  const handleRejectChanges = async () => {
    setActionLoading(true);
    try {
      await api.put(`/coordinator/mentors/${mentorId}/reject-changes`);
      toast.success('Profile changes rejected — existing profile retained'); fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setActionLoading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#F97D6A]" /></div>;
  if (error) return (
    <div className="max-w-2xl mx-auto py-16 px-4 text-center">
      <p className="text-sm text-red-600 mb-4">{error}</p>
      <Button variant="outline" onClick={() => navigate('/coordinator')} className="rounded-xl"><ArrowLeft className="h-4 w-4 mr-2" />Back to Dashboard</Button>
    </div>
  );
  if (!detail) return null;

  const st = detail.approval_status;
  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User className="h-3.5 w-3.5" /> },
    { id: 'experiences', label: 'Experiences', icon: <CalendarIcon className="h-3.5 w-3.5" /> },
    { id: 'safety', label: 'Safety & Approval', icon: <ShieldCheck className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F8F9FA]" data-testid="mentor-details-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <button onClick={() => navigate('/coordinator', { state: { section: 'mentors' } })} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#1F2937] mb-3 transition-colors" data-testid="back-to-mentors">
            <ArrowLeft className="h-4 w-4" /> Back to Mentors
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#F97D6A] to-[#F9A88C] flex items-center justify-center text-white font-bold text-lg shrink-0">
                {(detail.parent_name || '?')[0]}
              </div>
              <div>
                <h1 className="text-lg font-bold text-[#1F2937] font-['Outfit']" data-testid="mentor-name">{detail.parent_name}</h1>
                <p className="text-sm text-[#6B7280]">{detail.profession_title}{detail.company_name ? ` | ${detail.company_name}` : ''}</p>
                <p className="text-sm text-[#4B5563] mt-0.5" data-testid="mentor-children">
                  {(detail.children || []).length === 0 ? (
                    <span className="text-[#9CA3AF]">No children linked at this school</span>
                  ) : (
                    <>
                      <span className="text-[#6B7280]">Children: </span>
                      {detail.children.map(c => {
                        const cls = [c.grade, c.division].filter(Boolean).join('-');
                        return `${c.name}${cls ? ` (${cls})` : ''}`;
                      }).join(', ')}
                    </>
                  )}
                </p>
              </div>
              <Badge className={`border-0 text-xs ml-2 ${STATUS_COLORS[st]}`} data-testid="mentor-status">{st}</Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {st === 'pending' && <>
                <Button variant="outline" onClick={handleReject} disabled={actionLoading} className="rounded-xl text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-mentor">Reject</Button>
                <Button onClick={() => { setApproveConfirm(true); setSuitabilityChecked(false); }} className="rounded-xl text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-mentor">Approve</Button>
              </>}
              {st === 'approved' && <>
                <Button variant="outline" onClick={() => { setSuspendDialog(true); setSuspendReason(''); }} className="rounded-xl text-xs h-8 text-orange-600 border-orange-200 hover:bg-orange-50" data-testid="suspend-mentor-btn"><Ban className="h-3 w-3 mr-1" />Suspend</Button>
                <Button variant="outline" onClick={() => { setDeactivateDialog(true); setDeactivateReason(''); }} className="rounded-xl text-xs h-8 text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="deactivate-mentor-btn"><Power className="h-3 w-3 mr-1" />Deactivate</Button>
              </>}
              {st === 'rejected' && <Button onClick={handleReconsider} disabled={actionLoading} className="rounded-xl text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="reconsider-mentor"><RefreshCw className="h-3 w-3 mr-1" />Reconsider Application</Button>}
              {st === 'suspended' && <>
                <Button onClick={() => { setReinstateDialog(true); setReinstateReason(''); }} className="rounded-xl text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reinstate-mentor-btn"><RotateCcw className="h-3 w-3 mr-1" />Reinstate</Button>
                <Button variant="outline" onClick={() => { setDeactivateDialog(true); setDeactivateReason(''); }} className="rounded-xl text-xs h-8 text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="deactivate-mentor-btn"><Power className="h-3 w-3 mr-1" />Deactivate</Button>
              </>}
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-transparent -mb-[1px]">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${tab === t.id ? 'text-[#F97D6A] border-[#F97D6A] bg-white' : 'text-[#6B7280] border-transparent hover:text-[#1F2937]'}`}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Eligibility banner */}
        {detail.eligibility === 'action_required' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-5 flex items-start gap-3" data-testid="eligibility-action-required">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Action Required — Not Eligible for Assignments</p>
              <ul className="text-xs text-amber-700 mt-1 space-y-0.5">{detail.outstanding_requirements?.map((r, i) => <li key={i}>• {r}</li>)}</ul>
            </div>
          </div>
        )}
        {detail.eligibility === 'eligible' && st === 'approved' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-3" data-testid="eligibility-eligible">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
            <p className="text-sm font-medium text-emerald-800">Eligible for Assignments</p>
          </div>
        )}

        {/* Pending Profile Changes */}
        {detail.has_pending_changes && detail.pending_profile_changes && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-5" data-testid="pending-changes-review">
            <h3 className="text-sm font-bold text-blue-800 font-['Outfit'] mb-3 flex items-center gap-2">
              <Briefcase className="h-4 w-4" /> Profile Update Pending Review
            </h3>
            <p className="text-xs text-blue-600 mb-4">This mentor has submitted proposed changes to their profile. Compare with the current information and approve or reject.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {detail.pending_profile_changes.profession_title !== undefined && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#9CA3AF] uppercase">Profession</p>
                  <p className="text-sm text-[#6B7280] line-through">{detail.profession_title}</p>
                  <p className="text-sm font-medium text-blue-800">{detail.pending_profile_changes.profession_title}</p>
                </div>
              )}
              {detail.pending_profile_changes.company_name !== undefined && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#9CA3AF] uppercase">Company</p>
                  <p className="text-sm text-[#6B7280] line-through">{detail.company_name}</p>
                  <p className="text-sm font-medium text-blue-800">{detail.pending_profile_changes.company_name}</p>
                </div>
              )}
              {detail.pending_profile_changes.years_of_experience !== undefined && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#9CA3AF] uppercase">Years of Experience</p>
                  <p className="text-sm text-[#6B7280] line-through">{detail.years_of_experience} years</p>
                  <p className="text-sm font-medium text-blue-800">{detail.pending_profile_changes.years_of_experience} years</p>
                </div>
              )}
              {detail.pending_profile_changes.bio !== undefined && (
                <div className="bg-white rounded-lg p-3 sm:col-span-2">
                  <p className="text-xs font-semibold text-[#9CA3AF] uppercase">Biography</p>
                  <p className="text-sm text-[#6B7280] line-through mb-1">{detail.bio?.slice(0, 100)}{detail.bio?.length > 100 ? '...' : ''}</p>
                  <p className="text-sm text-blue-800">{detail.pending_profile_changes.bio}</p>
                </div>
              )}
              {detail.pending_profile_changes.mapped_careers && (
                <div className="bg-white rounded-lg p-3 sm:col-span-2">
                  <p className="text-xs font-semibold text-[#9CA3AF] uppercase mb-1">Proposed Career Mapping</p>
                  <div className="flex flex-wrap gap-1">
                    {detail.pending_profile_changes.mapped_careers.map(c => (
                      <Badge key={c.slug} className="bg-blue-100 text-blue-700 border-0 text-[10px]">{c.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleRejectChanges} disabled={actionLoading} className="rounded-xl text-xs h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-changes-btn">Reject Changes</Button>
              <Button onClick={handleApproveChanges} disabled={actionLoading} className="rounded-xl text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="approve-changes-btn">
                {actionLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />} Approve Changes
              </Button>
            </div>
          </div>
        )}

        {tab === 'profile' && <ProfileTab detail={detail} />}
        {tab === 'experiences' && <ExperiencesTab experiences={detail.experiences || []} />}
        {tab === 'safety' && <SafetyTab detail={detail} />}
      </div>

      {/* Approve Confirmation */}
      <Dialog open={approveConfirm} onOpenChange={o => { if (!o) { setApproveConfirm(false); setSuitabilityChecked(false); } }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="approve-confirm-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Approve Mentor</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-[#F8F9FA] rounded-xl p-3 space-y-1.5">
              <div className="flex justify-between"><span className="text-[#9CA3AF]">Mentor</span><span className="font-medium text-[#1F2937]">{detail.parent_name}</span></div>
              <div className="flex justify-between"><span className="text-[#9CA3AF]">Profession</span><span className="font-medium text-[#1F2937]">{detail.profession_title}</span></div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="suitability-checkbox-label">
              <input type="checkbox" checked={suitabilityChecked} onChange={e => setSuitabilityChecked(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-[#D1D5DB]" data-testid="suitability-checkbox" />
              <span className="text-sm text-[#4B5563] leading-relaxed">I confirm that this mentor belongs to our school parent community and has completed the suitability checks required by our school.</span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setApproveConfirm(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleApprove} disabled={!suitabilityChecked || actionLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="confirm-approve-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Approve Mentor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend */}
      <Dialog open={suspendDialog} onOpenChange={o => { if (!o) setSuspendDialog(false); }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="suspend-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Suspend Mentor</DialogTitle></DialogHeader>
          <div className="space-y-3"><Label className="text-sm text-[#6B7280]">Reason *</Label><Input value={suspendReason} onChange={e => setSuspendReason(e.target.value)} placeholder="Enter reason..." className="rounded-xl" data-testid="suspend-reason-input" /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSuspendDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSuspend} disabled={!suspendReason.trim() || actionLoading} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl" data-testid="confirm-suspend-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reinstate */}
      <Dialog open={reinstateDialog} onOpenChange={o => { if (!o) setReinstateDialog(false); }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="reinstate-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Reinstate Mentor</DialogTitle></DialogHeader>
          <div className="space-y-3"><Label className="text-sm text-[#6B7280]">Reason *</Label><Input value={reinstateReason} onChange={e => setReinstateReason(e.target.value)} placeholder="Enter reason..." className="rounded-xl" data-testid="reinstate-reason-input" /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReinstateDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleReinstate} disabled={!reinstateReason.trim() || actionLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="confirm-reinstate-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Reinstate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate */}
      <Dialog open={deactivateDialog} onOpenChange={o => { if (!o) setDeactivateDialog(false); }}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="deactivate-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Deactivate Mentor</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280]">Deactivation permanently removes the mentor from active duty. Existing bookings and history will be preserved.</p>
          <div className="space-y-3"><Label className="text-sm text-[#6B7280]">Reason *</Label><Input value={deactivateReason} onChange={e => setDeactivateReason(e.target.value)} placeholder="Enter reason..." className="rounded-xl" data-testid="deactivate-reason-input" /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateDialog(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleDeactivate} disabled={!deactivateReason.trim() || actionLoading} className="bg-slate-600 hover:bg-slate-700 text-white rounded-xl" data-testid="confirm-deactivate-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Profile Tab ───
function ProfileTab({ detail }) {
  return (
    <div className="space-y-6" data-testid="profile-tab">
      <MentorImpact impact={detail.impact} feedback={detail.student_feedback || []} />

      {/* Professional Information */}
      <Card title="Professional Information" icon={<Briefcase className="h-4 w-4 text-[#F97D6A]" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          <Field label="Full Name" value={detail.parent_name} />
          <Field label="Email" value={detail.parent_email} href={`mailto:${detail.parent_email}`} />
          <Field label="Profession / Job Title" value={detail.profession_title} />
          <Field label="Company / Workplace" value={detail.company_name} />
          <Field label="Years of Experience" value={detail.years_of_experience ? `${detail.years_of_experience} years` : null} />
          {detail.linkedin_url && <Field label="LinkedIn" value={detail.linkedin_url} href={detail.linkedin_url} truncate />}
        </div>
        {detail.bio && (
          <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
            <span className="text-xs font-semibold text-[#9CA3AF] uppercase">Professional Biography</span>
            <p className="text-sm text-[#4B5563] mt-2 leading-relaxed whitespace-pre-line break-words">{detail.bio}</p>
          </div>
        )}
        {detail.notes_for_coordinator && (
          <div className="mt-3 bg-amber-50 rounded-xl px-4 py-3">
            <span className="text-xs font-semibold text-amber-700 uppercase">Notes for Coordinator</span>
            <p className="text-sm text-amber-800 mt-1">{detail.notes_for_coordinator}</p>
          </div>
        )}
      </Card>

      {/* Career Mapping */}
      <Card title="Career Mapping" icon={<GraduationCap className="h-4 w-4 text-[#F97D6A]" />}>
        {detail.mapped_careers?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {detail.mapped_careers.map(c => (
              <div key={c.slug} className="bg-blue-50 rounded-lg px-3 py-2">
                <p className="text-sm font-medium text-blue-800">{c.name}</p>
                {c.category && <p className="text-[10px] text-blue-500">{c.category}</p>}
              </div>
            ))}
          </div>
        ) : <Empty text="No careers mapped" />}
      </Card>

      {/* Online Availability */}
      <Card title="Online Availability" icon={<Clock className="h-4 w-4 text-[#F97D6A]" />}>
        {detail.availability_config?.length > 0 ? (
          <div className="space-y-2">
            {detail.availability_config.map((d, i) => (
              <div key={i} className="flex items-center gap-4">
                <span className="w-24 text-sm font-medium text-[#1F2937]">{d.day}</span>
                <div className="flex gap-2">
                  {(d.time_ranges || []).map((t, j) => (
                    <span key={j} className="bg-[#F8F9FA] text-[#4B5563] px-3 py-1 rounded-lg text-sm">{t.start} – {t.end}{t.duration_minutes ? ` · ${t.duration_minutes} min` : ''}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <Empty text="No availability configured" />}
      </Card>

      {/* Workplace Visit Offerings */}
      {detail.offline_capabilities?.length > 0 && (
        <Card title="Workplace Visit Offerings" icon={<MapPin className="h-4 w-4 text-[#F97D6A]" />}>
          <div className="space-y-3">
            {detail.offline_capabilities.map((oc, i) => (
              <div key={i} className="border border-[#E5E7EB] rounded-xl px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#1F2937]">{oc.workplace_name}</p>
                    <p className="text-sm text-[#6B7280] mt-0.5">{oc.career_slug} · {oc.city}</p>
                  </div>
                  {oc.status && <Badge className={`border-0 text-[10px] ${STATUS_COLORS[oc.status] || 'bg-gray-100 text-gray-500'}`}>{oc.status}</Badge>}
                </div>
                {oc.description && <p className="text-sm text-[#6B7280] mt-2">{oc.description}</p>}
                <p className="text-xs text-[#9CA3AF] mt-1">{oc.date} · {oc.start_time}–{oc.end_time} · {oc.max_students} student{oc.max_students !== 1 ? 's' : ''} max</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Mentor Impact ───
const EXPERIENCE_TYPE_LABELS = [
  ['online_sessions', 'Online Sessions'],
  ['workplace_visits', 'Workplace Visits'],
];

function MentorImpact({ impact, feedback }) {
  const im = impact || {};
  const byType = im.by_type || {};
  const types = EXPERIENCE_TYPE_LABELS.filter(([k]) => byType[k] > 0);

  return (
    <Card title="Mentor Impact" icon={<Award className="h-4 w-4 text-[#F97D6A]" />}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mentor-impact">
        <div className="bg-[#F8F9FA] rounded-xl px-4 py-3" data-testid="impact-completed">
          <p className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{im.completed_experiences || 0}</p>
          <p className="text-[11px] text-[#6B7280] mt-0.5">Completed mentoring experiences</p>
        </div>
        <div className="bg-[#F8F9FA] rounded-xl px-4 py-3" data-testid="impact-participations">
          <p className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{im.student_participations || 0}</p>
          <p className="text-[11px] text-[#6B7280] mt-0.5">Student participations</p>
        </div>
        <div className="bg-[#F8F9FA] rounded-xl px-4 py-3" data-testid="impact-students">
          <p className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{im.students_reached || 0}</p>
          <p className="text-[11px] text-[#6B7280] mt-0.5">Students reached</p>
        </div>
        <div className="bg-[#F8F9FA] rounded-xl px-4 py-3" data-testid="impact-rating">
          <p className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-1.5">
            {im.rating ? <><Star className="h-4 w-4 text-[#F5A623] fill-[#F5A623]" />{im.rating}</> : <span className="text-[#9CA3AF]">—</span>}
          </p>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            {im.rating_count ? `Overall rating (${im.rating_count} review${im.rating_count === 1 ? '' : 's'})` : 'No ratings yet'}
          </p>
        </div>
      </div>

      {types.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
          <span className="text-xs font-semibold text-[#9CA3AF] uppercase">By Experience Type</span>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="impact-by-type">
            {types.map(([k, label]) => (
              <span key={k} className="bg-white border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm text-[#4B5563]">
                {label} <span className="font-semibold text-[#1F2937] ml-1">{byType[k]}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
        <span className="text-xs font-semibold text-[#9CA3AF] uppercase">Student Feedback</span>
        {feedback.length === 0 ? (
          <p className="text-sm text-[#9CA3AF] mt-2" data-testid="impact-no-feedback">No feedback submitted yet</p>
        ) : (
          <div className="space-y-2 mt-2" data-testid="impact-feedback">
            {feedback.map((f, i) => (
              <div key={i} className="bg-[#F8F9FA] rounded-xl px-4 py-3" data-testid={`impact-feedback-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-[#1F2937]">{f.child_name}{f.career_name ? ` · ${f.career_name}` : ''}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-[#1F2937] shrink-0">
                    <Star className="h-3 w-3 text-[#F5A623] fill-[#F5A623]" />{f.rating}
                  </span>
                </div>
                {f.comment && <p className="text-sm text-[#4B5563] mt-1 leading-relaxed">{f.comment}</p>}
                {f.submitted_at && <p className="text-[10px] text-[#9CA3AF] mt-1">{fmtD(f.submitted_at)}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Experiences Tab ───
function ExperiencesTab({ experiences }) {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const now = new Date().toISOString().split('T')[0];
  const filtered = experiences.filter(e => {
    if (filter === 'upcoming') return e.assigned_date >= now && e.status !== 'completed' && e.status !== 'cancelled';
    if (filter === 'past') return e.assigned_date < now || e.status === 'completed';
    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [filter]);

  return (
    <div data-testid="experiences-tab">
      <div className="flex items-center gap-2 mb-4">
        {['all', 'upcoming', 'past'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${filter === f ? 'bg-[#F97D6A] text-white' : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:border-[#F97D6A]'}`}
            data-testid={`exp-filter-${f}`}>
            {f === 'all' ? `All (${experiences.length})` : f === 'upcoming' ? 'Upcoming' : 'Past'}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card><Empty text={filter === 'all' ? 'No experiences assigned to this mentor' : `No ${filter} experiences`} /></Card>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <table className="w-full text-sm" data-testid="experiences-table">
            <thead className="bg-[#F8F9FA]">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Date</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Career</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280] hidden md:table-cell">Student</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280] hidden sm:table-cell">Mode</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#6B7280]">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(e => (
                <tr key={e.id} className="border-t border-[#F3F4F6] hover:bg-[#FAFAFA] transition-colors" data-testid={`exp-row-${e.id}`}>
                  <td className="px-4 py-2.5 text-[#1F2937]">{fmtD(e.assigned_date)}{e.assigned_time ? <span className="text-[#9CA3AF] ml-1 text-xs">{e.assigned_time}</span> : ''}</td>
                  <td className="px-4 py-2.5 font-medium text-[#1F2937]">{e.career_name || '—'}</td>
                  <td className="px-4 py-2.5 text-[#4B5563] hidden md:table-cell">{e.child_name || '—'}</td>
                  <td className="px-4 py-2.5 text-[#4B5563] hidden sm:table-cell">{e.mode ? modeLabelFor(e.mode) : '—'}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`border-0 text-[10px] ${BOOKING_STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-500'}`}>
                      {BOOKING_STATUS_LABELS[e.status] || e.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-[#9CA3AF]">Page {page} of {totalPages} · {filtered.length} experience{filtered.length !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-md hover:bg-[#F3F4F6] disabled:opacity-30 text-[#6B7280]" data-testid="exp-prev-page"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-md hover:bg-[#F3F4F6] disabled:opacity-30 text-[#6B7280]" data-testid="exp-next-page"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Safety & Approval Tab ───
function SafetyTab({ detail }) {
  const approvalRecord = detail.action_history?.find(h => h.action === 'approved' && h.suitability_confirmed);
  const hasCurrentDecl = detail.declaration_records?.some(d => d.version === '1.0' && d.status === 'accepted');
  const needsReacceptance = detail.approval_status === 'approved' && !hasCurrentDecl;

  return (
    <div className="space-y-6" data-testid="safety-tab">
      {/* Eligibility Warning */}
      {needsReacceptance && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="reacceptance-warning">
          <ShieldCheck className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Declaration re-acceptance required</p>
            <p className="text-xs text-amber-600 mt-0.5">This mentor was approved before the current safeguarding declaration (v1.0) was introduced. They should accept the updated declaration before receiving new assignments.</p>
          </div>
        </div>
      )}

      {/* Safeguarding Declaration */}
      <Card title="Safeguarding Declaration" icon={<ShieldCheck className="h-4 w-4 text-[#F97D6A]" />}>
        {detail.declaration_records?.length > 0 ? (
          <div className="space-y-3">
            {detail.declaration_records.map((d, i) => (
              <div key={i} className="flex items-start justify-between gap-4 py-2 border-b border-[#F3F4F6] last:border-0">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge className="border-0 bg-emerald-50 text-emerald-600 text-[10px]">Accepted</Badge>
                    <span className="text-sm text-[#6B7280]">Version {d.version}</span>
                  </div>
                  <p className="text-xs text-[#9CA3AF] mt-1">Accepted on {fmtDT(d.accepted_at)} · Method: {d.method}</p>
                </div>
              </div>
            ))}
          </div>
        ) : detail.safety_agreement ? (
          <div className="flex items-center gap-3">
            <Badge className="border-0 bg-amber-50 text-amber-700 text-xs">Legacy Acceptance</Badge>
            <p className="text-xs text-[#9CA3AF]">Accepted before the current versioned declaration was introduced. No version or timestamp recorded.</p>
          </div>
        ) : (
          <Empty text="Safeguarding declaration not accepted" />
        )}
      </Card>

      {/* School Suitability Verification */}
      <Card title="School Suitability Verification" icon={<Building2 className="h-4 w-4 text-[#F97D6A]" />}>
        {approvalRecord ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            <Field label="Verification" value={<span className="text-emerald-600 font-medium">Confirmed by school</span>} />
            <Field label="Approved By" value={approvalRecord.coordinator_name} />
            <Field label="Approved On" value={fmtDT(approvalRecord.timestamp)} />
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#9CA3AF]">Not recorded</span>
            {detail.approval_status === 'approved' && <p className="text-xs text-[#9CA3AF]">This mentor was approved before suitability confirmation was introduced.</p>}
          </div>
        )}
      </Card>

      {/* Application & Status History */}
      <Card title="Application & Status History" icon={<History className="h-4 w-4 text-[#F97D6A]" />}>
        <div className="space-y-3">
          {/* Core dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 pb-3 border-b border-[#F3F4F6]">
            <Field label="Application Created" value={fmtD(detail.created_at)} />
            {detail.submitted_at && <Field label="Application Submitted" value={fmtDT(detail.submitted_at)} />}
          </div>

          {/* Action history */}
          {detail.action_history?.length > 0 ? (
            <div className="space-y-3">
              {detail.action_history.map((h, i) => {
                const actionColors = { approved: 'bg-emerald-50 text-emerald-700', rejected: 'bg-red-50 text-red-700', suspended: 'bg-orange-50 text-orange-700', reinstated: 'bg-blue-50 text-blue-700', deactivated: 'bg-slate-100 text-slate-700', reconsidered: 'bg-violet-50 text-violet-700' };
                return (
                  <div key={i} className="flex items-start gap-3">
                    <Badge className={`border-0 text-[10px] shrink-0 mt-0.5 ${actionColors[h.action] || 'bg-gray-100 text-gray-600'}`}>{h.action}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1F2937]">
                        <span className="font-medium">{h.coordinator_name || 'System'}</span>
                        <span className="text-[#9CA3AF] ml-2">{fmtDT(h.timestamp)}</span>
                      </p>
                      {h.reason && <p className="text-sm text-[#6B7280] mt-0.5 italic">"{h.reason}"</p>}
                      {h.suitability_confirmed && <p className="text-xs text-emerald-600 mt-0.5">School suitability confirmed</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-[#9CA3AF]">No status history recorded</p>}
        </div>
      </Card>
    </div>
  );
}

// ─── Shared Components ───
function Card({ title, icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 sm:p-6">
      {title && (
        <h3 className="text-sm font-bold text-[#1F2937] mb-4 flex items-center gap-2 font-['Outfit']">
          {icon} {title}
        </h3>
      )}
      {children}
    </div>
  );
}

function Field({ label, value, href, truncate }) {
  const display = value || <span className="text-[#D1D5DB]">Not provided</span>;
  return (
    <div>
      <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{label}</span>
      <p className={`text-sm text-[#1F2937] mt-0.5 ${truncate ? 'truncate' : ''}`}>
        {href ? <a href={href} target="_blank" rel="noreferrer" className="text-[#F97D6A] hover:underline break-all">{typeof display === 'string' ? display : display}</a> : display}
      </p>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-[#9CA3AF] py-2">{text}</p>;
}
